/* Лестница целей в обзоре (ТЗ 5.9): ступени кольцами, по порядку слева направо.
   Она же заняла место шкалы кэшфлоу, поэтому выделена тем же синим акцентом:
   этот блок показывает не тревогу, а ход к цели.

   Закрытая ступень — галочка, текущая — её номер, остальные — замок: они ещё
   не стартовали. Прогресс кольца рисуем и у закрытых замком: видно, что цифра
   подрастает сама, хотя очередь до неё не дошла.

   Ступени делят доступную ширину поровну, и всё внутри ступени — кольцо, цифра,
   подписи — меряется от неё же: лестница из трёх и из шести целей одинаково
   укладывается в карточку. Совсем узкие ступени не сжимаем, там уже ничего
   не разобрать — вместо этого ряд начинает прокручиваться вбок. */

import { goalColor, shortNum } from "@mera/shared";
import React, { useLayoutEffect, useRef, useState } from "react";

import { Ion } from "./icons.jsx";

const TRACK = "color-mix(in srgb, var(--tg-hint) 18%, transparent)";
const MUTED = "color-mix(in srgb, var(--tg-hint) 75%, transparent)";
const ACCENT_LINE = "color-mix(in srgb, var(--tg-button) 24%, transparent)";
const ACCENT_BG = "color-mix(in srgb, var(--tg-button) 7%, var(--tg-section-bg))";

/* Ступень в полную ширину и её части — от них считаются все размеры */
const STEP = 76;
const RING = 62;
const CHEVRON = 18;
const MIN_STEP = 48;

/** Планка цели словами: доход всегда за месяц, накопления — просто сумма */
export const goalTargetLabel = (g) => shortNum(g.goal) + (g.kind === "income" ? "/мес" : "");

/* Кольцо прогресса: дуга — штриховка окружности, поэтому нулевой прогресс
   оставляет кольцо честно пустым, а не рисует точку у начала. */
function Ring({ t, color, size, children }) {
  const stroke = Math.max(3, Math.round(size / 12));
  const r = (size - stroke) / 2;
  const len = 2 * Math.PI * r;
  const c = size / 2;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={c} cy={c} r={r} fill="none" strokeWidth={stroke} style={{ stroke: TRACK }} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${len * t} ${len}`}
          /* Без анимации: кольцо рисуется сразу на своём значении. Заполнение
             от нуля при каждом открытии обзора читалось как «прогресс идёт прямо
             сейчас», хотя это просто отрисовка. */
          style={{ stroke: color }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

function Step({ goal, width, k }) {
  const done = goal.state === "done";
  const active = goal.state === "active";
  // Цвет цели горит только у того, что уже идёт; ждущая ступень серая
  const color = done || active ? goalColor(goal) : MUTED;
  const ring = Math.round(RING * k);

  return (
    <div className="shrink-0 flex flex-col items-center text-center" style={{ width }}>
      <Ring t={goal.t} color={color} size={ring}>
        {done ? (
          <Ion.checkmark size={Math.round(26 * k)} style={{ color }} />
        ) : active ? (
          <span
            className="num font-bold leading-none"
            style={{ color, fontSize: Math.round(22 * k) }}
          >
            {goal.step}
          </span>
        ) : (
          <Ion.lockClosed size={Math.round(20 * k)} style={{ color: MUTED }} />
        )}
      </Ring>
      <div
        className="num mt-2 font-bold leading-tight whitespace-nowrap"
        style={{
          fontSize: Math.max(11, Math.round(13 * k)),
          color: done || active ? "var(--tg-text)" : "var(--tg-hint)",
        }}
      >
        {goalTargetLabel(goal)}
      </div>
      <div
        className="mt-0.5 w-full truncate leading-tight"
        style={{ fontSize: Math.max(10, Math.round(12 * k)), color: "var(--tg-hint)" }}
      >
        {goal.name}
      </div>
    </div>
  );
}

export default function GoalLadder({ goals, onOpen }) {
  const row = useRef(null);
  const [width, setWidth] = useState(0);

  /* Ширину меряем у самого ряда: она зависит и от экрана, и от полей страницы,
     и от того, во что карточку положили. ResizeObserver отдаёт её сразу при
     подписке — до первой отрисовки, поэтому крупными кольцами кадр не мигает */
  useLayoutEffect(() => {
    const el = row.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!goals?.length) return null;

  const n = goals.length;
  /* Ступени делят всю ширину поровну — ряд всегда занимает карточку целиком.
     Шире эталона ступень быть может (кольца просто разъедутся), уже MIN_STEP —
     нет: там уже ничего не разобрать, и вместо сжатия ряд едет вбок. */
  const step = width ? Math.max(MIN_STEP, (width - (n - 1) * CHEVRON) / n) : MIN_STEP;
  const k = Math.min(1, step / STEP);

  return (
    <div className="px-4 pt-2">
      <div
        className={`card-tg px-4 py-3.5 ${onOpen ? "press" : ""}`}
        style={{ background: ACCENT_BG, boxShadow: `inset 0 0 0 1px ${ACCENT_LINE}` }}
        onClick={onOpen || undefined}
        role={onOpen ? "button" : undefined}
      >
        <div
          className="eyebrow px-0 pt-0 pb-2.5 flex items-center gap-1.5"
          style={{ color: "var(--tg-button)" }}
        >
          <Ion.locate size={13} />
          Путь к цели
        </div>
        {/* Ступени влезли — ряд стоит на месте; целей слишком много — прокручивается */}
        <div ref={row} className="hscroll -mx-4 px-4 flex items-start">
          {goals.map((g, i) => (
            <React.Fragment key={g.id}>
              {/* Стрелка встаёт по центру колец, а не по верху подписей */}
              {i > 0 ? (
                <span
                  className="shrink-0 flex justify-center"
                  style={{ width: CHEVRON, marginTop: Math.round(k * (RING - 15) / 2) }}
                >
                  <Ion.chevronForward size={Math.round(15 * k)} style={{ color: MUTED }} />
                </span>
              ) : null}
              <Step goal={g} width={step} k={k} />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
